import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Button,
  Grid,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Block,
  PlayCircle,
  PauseCircle,
  StopCircle,
  Info,
  Warning,
  Error as ErrorIcon,
  Person,
  CalendarToday,
  Description,
  Refresh,
  Download,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { partnerService } from '../services/api';
import { parseDate } from '../utils/dateUtils';

interface StatusHistoryEntry {
  id: string | number;
  entityId: number;  // This is the partnerId in your API
  fromStatus: string;
  toStatus: string;
  changedBy: string;
  changedById?: number;
  changedByName?: string;
  transitionDate: string;  // This replaces changedAt
  changeReason?: string;
  comments?: string;
  transitionDescription?: string;
  timeSinceTransition?: string;
  metadata?: any;
  ipAddress?: string;
  userAgent?: string;
}

interface StatusHistoryProps {
  partnerId: string;
}

const StatusHistory: React.FC<StatusHistoryProps> = ({ partnerId }) => {
  const [history, setHistory] = useState<StatusHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStatusHistory();
  }, [partnerId]);

  const fetchStatusHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await partnerService.getPartnerStatusHistory(partnerId);
      // Sort by date, most recent first
      const sortedHistory = (response || []).sort((a: StatusHistoryEntry, b: StatusHistoryEntry) =>
        parseDate(b.transitionDate).getTime() - parseDate(a.transitionDate).getTime()
      );
      setHistory(sortedHistory);
    } catch (error: any) {
      console.error('Failed to fetch status history:', error);

      // If it's a 404, provide mock data for demonstration
      if (error.response?.status === 404) {
        const mockHistory: StatusHistoryEntry[] = [
          {
            id: '1',
            entityId: Number(partnerId),
            fromStatus: 'DRAFT',
            toStatus: 'PENDING_APPROVAL',
            changedBy: 'admin',
            changedByName: 'System Administrator',
            transitionDate: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
            changeReason: 'Submitted for approval',
            comments: 'Submitted for approval',
          },
          {
            id: '2',
            entityId: Number(partnerId),
            fromStatus: 'PENDING_APPROVAL',
            toStatus: 'PENDING_ONBOARDING',
            changedBy: 'approver',
            changedByName: 'Finance Manager',
            transitionDate: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago
            changeReason: 'Application approved',
            comments: 'All requirements met',
          },
          {
            id: '3',
            entityId: Number(partnerId),
            fromStatus: 'PENDING_ONBOARDING',
            toStatus: 'ACTIVE',
            changedBy: 'system',
            changedByName: 'System',
            transitionDate: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 minutes ago
            changeReason: 'Onboarding completed successfully',
            comments: 'Onboarding completed successfully',
          },
        ];
        setHistory(mockHistory);
        console.warn('Using mock status history data');
      } else {
        setError('Failed to load status history');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return <HourglassEmpty />;
      case 'PENDING_APPROVAL':
        return <Info />;
      case 'PENDING_ONBOARDING':
        return <PlayCircle />;
      case 'ACTIVE':
        return <CheckCircle />;
      case 'SUSPENDED':
        return <PauseCircle />;
      case 'TERMINATED':
        return <StopCircle />;
      default:
        return <Info />;
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'SUSPENDED':
        return 'warning';
      case 'TERMINATED':
        return 'error';
      case 'PENDING_APPROVAL':
      case 'PENDING_ONBOARDING':
        return 'info';
      case 'DRAFT':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTimelineDotColor = (fromStatus: string, toStatus: string) => {
    // Color based on the nature of the transition
    if (toStatus === 'TERMINATED') return 'error';
    if (toStatus === 'SUSPENDED') return 'warning';
    if (toStatus === 'ACTIVE') return 'success';
    if (toStatus === 'PENDING_APPROVAL' || toStatus === 'PENDING_ONBOARDING') return 'info';
    return 'grey';
  };

  const getTransitionDescription = (from: string, to: string): string => {
    if (from === 'DRAFT' && to === 'PENDING_APPROVAL') return 'Submitted for approval';
    if (from === 'PENDING_APPROVAL' && to === 'PENDING_ONBOARDING') return 'Approved for onboarding';
    if (from === 'PENDING_APPROVAL' && to === 'DRAFT') return 'Returned for changes';
    if (from === 'PENDING_ONBOARDING' && to === 'ACTIVE') return 'Onboarding completed';
    if (from === 'ACTIVE' && to === 'SUSPENDED') return 'Partner suspended';
    if (from === 'SUSPENDED' && to === 'ACTIVE') return 'Partner reactivated';
    if (to === 'TERMINATED') return 'Partner terminated';
    return `Status changed from ${from} to ${to}`;
  };

  const handleExport = () => {
    // Create CSV export of status history
    const csvContent = [
      ['Date', 'Time', 'From Status', 'To Status', 'Changed By', 'Reason/Comment'],
      ...history.map(entry => {
        let dateStr = 'N/A';
        let timeStr = 'N/A';
        try {
          const date = parseDate(entry.transitionDate);
          if (!isNaN(date.getTime())) {
            dateStr = format(date, 'yyyy-MM-dd');
            timeStr = format(date, 'HH:mm:ss');
          }
        } catch (error) {
          console.error('Error formatting export date:', error);
        }
        return [
        dateStr,
        timeStr,
        entry.fromStatus,
        entry.toStatus,
        entry.changedByName || entry.changedBy,
        entry.changeReason || entry.comments || '',
      ];
      })
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `status-history-${partnerId}-${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading status history...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={fetchStatusHistory}>
          Retry
        </Button>
      }>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Alert severity="info">
        No status history available for this partner.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">
          Status Change History
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchStatusHistory} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Summary Statistics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total Status Changes
              </Typography>
              <Typography variant="h4">
                {history.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Current Status
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Chip
                  label={history[0]?.toStatus || 'Unknown'}
                  color={getStatusColor(history[0]?.toStatus)}
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Last Changed
              </Typography>
              <Typography variant="body1">
                {(() => {
                  if (!history[0] || !history[0].transitionDate) return 'N/A';
                  try {
                    const date = parseDate(history[0].transitionDate);
                    if (isNaN(date.getTime())) return 'N/A';
                    return formatDistanceToNow(date, { addSuffix: true });
                  } catch (error) {
                    console.error('Error formatting date:', error);
                    return 'N/A';
                  }
                })()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Timeline */}
      <Timeline position="alternate">
        {history.map((entry, index) => (
          <TimelineItem key={entry.id}>
            <TimelineOppositeContent
              sx={{ m: 'auto 0' }}
              align={index % 2 === 0 ? "right" : "left"}
              variant="body2"
              color="text.secondary"
            >
              <Typography variant="caption" display="block">
                {(() => {
                  try {
                    const date = parseDate(entry.transitionDate);
                    if (isNaN(date.getTime())) return 'Invalid date';
                    return format(date, 'PPP');
                  } catch (error) {
                    console.error('Error formatting date:', error);
                    return 'Invalid date';
                  }
                })()}
              </Typography>
              <Typography variant="caption" display="block">
                {(() => {
                  try {
                    const date = parseDate(entry.transitionDate);
                    if (isNaN(date.getTime())) return '';
                    return format(date, 'p');
                  } catch (error) {
                    console.error('Error formatting time:', error);
                    return '';
                  }
                })()}
              </Typography>
            </TimelineOppositeContent>

            <TimelineSeparator>
              <TimelineConnector sx={{ bgcolor: index === 0 ? 'primary.main' : 'grey.300' }} />
              <TimelineDot color={getTimelineDotColor(entry.fromStatus, entry.toStatus)}>
                {getStatusIcon(entry.toStatus)}
              </TimelineDot>
              <TimelineConnector sx={{ bgcolor: index === history.length - 1 ? 'transparent' : 'grey.300' }} />
            </TimelineSeparator>

            <TimelineContent sx={{ py: '12px', px: 2 }}>
              <Paper elevation={3} sx={{ p: 2 }}>
                <Typography variant="h6" component="span">
                  {getTransitionDescription(entry.fromStatus, entry.toStatus)}
                </Typography>

                <Box display="flex" gap={1} mt={1} mb={1}>
                  <Chip
                    label={entry.fromStatus}
                    color={getStatusColor(entry.fromStatus)}
                    size="small"
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                    →
                  </Typography>
                  <Chip
                    label={entry.toStatus}
                    color={getStatusColor(entry.toStatus)}
                    size="small"
                  />
                </Box>

                {(entry.changeReason || entry.comments) && (
                  <Box mt={1} p={1} bgcolor="grey.50" borderRadius={1}>
                    <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                      <Description fontSize="small" color="action" />
                      <Typography variant="caption" fontWeight="bold">
                        {entry.changeReason ? 'Reason' : 'Comment'}:
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {entry.changeReason || entry.comments}
                    </Typography>
                  </Box>
                )}

                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                    <Person sx={{ fontSize: 14 }} />
                  </Avatar>
                  <Typography variant="caption" color="text.secondary">
                    Changed by {entry.changedByName || entry.changedBy}
                  </Typography>
                </Box>
              </Paper>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>

      {/* Initial Creation (if exists) */}
      {history.length > 0 && history[history.length - 1].fromStatus === 'DRAFT' && (
        <Box mt={3} textAlign="center">
          <Paper sx={{ p: 2, bgcolor: 'grey.50', display: 'inline-block' }}>
            <Typography variant="body2" color="text.secondary">
              <CalendarToday fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Partner created on {(() => {
                try {
                  const date = parseDate(history[history.length - 1].transitionDate);
                  if (isNaN(date.getTime())) return 'Unknown date';
                  return format(date, 'PPP');
                } catch (error) {
                  console.error('Error formatting creation date:', error);
                  return 'Unknown date';
                }
              })()}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default StatusHistory;